<template>
    <div
        class="flex flex-col rounded-xl shadow-lg max-lg:max-w-lg max-w-sm mx-auto lg:max-xl:min-h-[48rem] bg-light-gray"
    >
        <div class="rounded-t-xl bg-yellow-500 px-5 sm:px-7 py-5">
            <div class="flex">
                <div class="flex flex-1 flex-col space-y-3 text-purple">
                    <span class="flex items-center font-bold text-gold lg:text-lg">
                        <img
                            src="/img/icon-star-premium-white-bg.svg"
                            alt="Premium star icon"
                            class="mr-2 inline-block h-6 w-6"
                        />
                        Premium Pack
                    </span>
                    <div class="flex items-center gap-2">
                        <span
                            class="bg-warning-red rounded-full px-2.5 py-1 text-white text-xs font-semibold uppercase"
                        >
                            Save {{ selectedPackProduct.percent_saving }}
                        </span>
                        <span class="text-purple text-lg line-through">{{ premiumThankboxPrice }}</span>
                    </div>
                    <span>
                        <span class="text-4xl lg:text-4.5xl font-semibold">{{
                            selectedPackProduct.price_per_card
                        }}</span>
                        <span class="ml-2 text-sm">
                            <strong>per card</strong> <template v-if="showPricesWithVat">(inc. VAT)</template>
                        </span>
                    </span>
                </div>
                <img
                    src="/img/emojis/emoji-packs.svg"
                    class="self-start max-sm:size-14"
                    width="69"
                    height="69"
                    alt=""
                />
            </div>

            <div class="flex flex-col space-y-1 mt-4 max-md:w-full">
                <span class="font-semibold text-purple">Choose Pack size:</span>
                <Multiselect
                    class="smaller-tag w-full"
                    :allow-empty="false"
                    :searchable="false"
                    v-model="selectedPackProduct"
                    :options="packProducts"
                    :show-labels="false"
                >
                    <template #singleLabel="{ option }">
                        <span class="text-lg"
                            ><strong>Pack of {{ option.total_credits }}</strong> - {{ option.price }}</span
                        >
                    </template>
                    <template #option="{ option }">
                        <span class="text-lg"
                            ><strong>Pack of {{ option.total_credits }}</strong> - {{ option.price }}</span
                        >
                    </template>
                </Multiselect>
            </div>
        </div>
        <div class="relative rounded-b-xl px-5 pt-4 text-dark-gray xl:px-8 lg:text-lg">
            <ul class="fa-ul icon-bullet-list gold mt-2 space-y-2">
                <li v-for="feature in sharedFeatures" :key="feature">
                    <i class="fa-solid fa-check-circle fa-li"></i>
                    <span class="ml-2" v-html="feature" />
                </li>
                <li>
                    <i class="fa-solid fa-check-circle fa-li"></i>
                    <span class="ml-2" :class="{ 'border-b-2 border-b-green': selectedPackProduct.months_valid > 12 }"
                        >Valid for <strong>{{ selectedPackProduct.months_valid }} months</strong></span
                    >
                </li>
            </ul>
            <p class="mt-4 text-sm font-bold text-purple" v-if="selectedPackProduct.extra_trees > 0">
                <i class="fa-solid fa-tree mr-1"></i>
                +{{ selectedPackProduct.extra_trees }}
                {{ selectedPackProduct.extra_trees > 1 ? 'trees' : 'tree' }} planted with this plan!
            </p>
        </div>
        <div class="flex flex-col gap-2 mt-5 px-5 xl:px-8 pb-8">
            <a href="#compare-features" class="button secondary px-6">Compare features</a>
            <a
                class="button px-12"
                :href="`/app/buy-pack/${selectedPackProduct.id}`"
                @click="trackFathomGoal('YPPIU3FU')"
            >
                Buy pack
            </a>
        </div>
    </div>
</template>
<script setup lang="ts">
import { computed, ref } from 'vue'
import Multiselect from 'vue-multiselect'
import trackFathomGoal from '@/util/trackFathomGoal'
import { DetailedPackProductOption } from 'resources/js/types'

const props = withDefaults(
    defineProps<{
        packProducts: DetailedPackProductOption[]
        premiumThankboxPrice: string
        showPricesWithVat: boolean
        showCheapestFirst?: boolean
        premiumCollectionLimit: string
    }>(),
    {
        showCheapestFirst: false,
    },
)

const sharedFeatures = computed(() => {
    return [
        '<strong>One payment</strong>, one invoice',
        'Add your <strong>own design & branding</strong>',
        'Add <strong>GIFs & photos</strong>',
        'Add <strong>videos & voice notes</strong>',
        '20+ message <strong>fonts options</strong>',
        `Gift collection limit <strong>${props.premiumCollectionLimit}</strong>`,
        '<strong>Bulk creation & scheduling</strong>',
    ]
})

const selectedPackProduct = ref<DetailedPackProductOption>(
    props.showCheapestFirst
        ? props.packProducts.sort((a, b) => a.total_credits - b.total_credits)[0]
        : props.packProducts.find(p => p.is_most_popular) || props.packProducts[0],
)
</script>
